/**
 * The interview's sections, in order, as a timeline the recruiter rearranges.
 *
 * Drag by the handle: the row follows the pointer 1:1 from where it was
 * grabbed, the others make room, and on release it settles into its slot
 * from where it was let go. From the keyboard, the same handle moves the
 * section with the up and down arrows, instantly, and a live region says
 * where it went. The opening and the close are fixed and drawn as bookends.
 */
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent,
} from 'react';
import type {
  InterviewerConfig,
  InterviewerPreview,
  SectionKey,
} from '../../services/interviewer.types';
import {
  ALWAYS_ON,
  FIXED_LAST,
  canStep,
  canToggleOff,
  lastMovableIndex,
} from '../../interviewer/model';
import { GripIcon, LockIcon, MinusIcon, PlusIcon } from './icons';
import styles from './index.module.css';

interface Props {
  config: InterviewerConfig;
  preview: InterviewerPreview;
  disabled: boolean;
  highlight: Set<string>;
  onToggle: (key: SectionKey) => void;
  onStep: (key: SectionKey, delta: number) => void;
  onMove: (from: number, to: number) => void;
}

interface DragState {
  from: number;
  over: number;
  dy: number;
}

/** What a section being off means, where it matters. */
const OFF_LINES: Partial<Record<SectionKey, string>> = {
  logistics:
    "Off. Rio won't ask about work authorisation or sponsorship; confirm them another way.",
};

/** The sections every interview keeps (Om, 27 Oct). */
const ALWAYS_ON_LINES: Partial<Record<SectionKey, string>> = {
  skills: 'Always on: every interview covers the skills for the role.',
  candidate_questions: 'Every candidate gets to ask about the role.',
};

const reduceMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const SectionTimeline = ({
  config,
  preview,
  disabled,
  highlight,
  onToggle,
  onStep,
  onMove,
}: Props) => {
  const uid = useId();
  const rows = useRef<(HTMLLIElement | null)[]>([]);
  const grips = useRef(new Map<SectionKey, HTMLButtonElement | null>());
  const gesture = useRef<{
    startY: number;
    from: number;
    rects: DOMRect[];
    gap: number;
  } | null>(null);
  const dropped = useRef<{ key: SectionKey; visualTop: number } | null>(null);
  const refocus = useRef<SectionKey | null>(null);
  const [drag, setDrag] = useState<DragState | null>(null);
  const [settling, setSettling] = useState(false);
  const [announcement, setAnnouncement] = useState('');

  const titleOf = useCallback(
    (key: SectionKey) =>
      preview.sections.find((s) => s.key === key)?.title ?? key,
    [preview.sections],
  );

  // After a drop: no transition for one frame, so the rows that made room
  // don't slide back; then the dropped row eases from where it was let go.
  useLayoutEffect(() => {
    if (!settling) return;
    const drop = dropped.current;
    dropped.current = null;
    if (drop && !reduceMotion()) {
      const index = config.sections.findIndex((s) => s.key === drop.key);
      const row = rows.current[index];
      if (row && typeof row.animate === 'function') {
        const delta = drop.visualTop - row.getBoundingClientRect().top;
        if (Math.abs(delta) > 1) {
          row.animate(
            [
              { transform: `translateY(${delta}px)` },
              { transform: 'translateY(0)' },
            ],
            { duration: 200, easing: 'cubic-bezier(0.23, 1, 0.32, 1)' },
          );
        }
      }
    }
    const frame = requestAnimationFrame(() => setSettling(false));
    return () => cancelAnimationFrame(frame);
  }, [settling, config.sections]);

  // Keyboard moves keep focus on the handle that moved.
  useEffect(() => {
    if (!refocus.current) return;
    grips.current.get(refocus.current)?.focus();
    refocus.current = null;
  }, [config.sections]);

  const announceMove = (key: SectionKey, to: number) =>
    setAnnouncement(
      `${titleOf(key)} moved to position ${to + 1} of ${config.sections.length}.`,
    );

  const handleKeyDown = (
    event: KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    if (disabled) return;
    const delta =
      event.key === 'ArrowUp' ? -1 : event.key === 'ArrowDown' ? 1 : 0;
    if (!delta) return;
    event.preventDefault();
    const to = index + delta;
    if (to < 0 || to > lastMovableIndex(config)) return;
    const key = config.sections[index].key;
    refocus.current = key;
    onMove(index, to);
    announceMove(key, to);
  };

  const handlePointerDown = (
    event: PointerEvent<HTMLButtonElement>,
    index: number,
  ) => {
    if (disabled || event.button !== 0) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    const rects = config.sections.map(
      (_, i) =>
        rows.current[i]?.getBoundingClientRect() ??
        ({ top: 0, bottom: 0, height: 0 } as DOMRect),
    );
    const gap =
      rects.length > 1 ? Math.max(0, rects[1].top - rects[0].bottom) : 0;
    gesture.current = { startY: event.clientY, from: index, rects, gap };
    setDrag({ from: index, over: index, dy: 0 });
  };

  const handlePointerMove = (event: PointerEvent<HTMLButtonElement>) => {
    const g = gesture.current;
    if (!g) return;
    const dy = event.clientY - g.startY;
    const centre = g.rects[g.from].top + g.rects[g.from].height / 2 + dy;
    let over = g.from;
    g.rects.forEach((rect, i) => {
      if (centre >= rect.top && centre <= rect.bottom) over = i;
    });
    if (centre < g.rects[0].top) over = 0;
    if (centre > g.rects[g.rects.length - 1].bottom) over = g.rects.length - 1;
    // Nothing goes below the candidate's questions.
    over = Math.min(over, lastMovableIndex(config));
    setDrag({ from: g.from, over, dy });
  };

  const finishDrag = (commit: boolean) => {
    const g = gesture.current;
    gesture.current = null;
    if (!g || !drag) {
      setDrag(null);
      return;
    }
    const key = config.sections[g.from].key;
    if (commit && drag.over !== g.from) {
      dropped.current = { key, visualTop: g.rects[g.from].top + drag.dy };
      setSettling(true);
      onMove(g.from, drag.over);
      announceMove(key, drag.over);
    }
    setDrag(null);
  };

  const shiftFor = (index: number): string | undefined => {
    if (!drag || !gesture.current) return undefined;
    const { from, over, dy } = drag;
    const g = gesture.current;
    const size = g.rects[from].height + g.gap;
    if (index === from) return `translateY(${dy}px) scale(1.01)`;
    if (from < over && index > from && index <= over)
      return `translateY(${-size}px)`;
    if (from > over && index >= over && index < from)
      return `translateY(${size}px)`;
    return 'translateY(0)';
  };

  return (
    <>
      <ol className={styles.timeline} aria-label="Interview sections, in order">
        <li className={`${styles.row} ${styles.rowFixed}`}>
          <span aria-hidden />
          <span className={styles.marker}>
            <span className={`${styles.dot} ${styles.dotFixed}`} />
          </span>
          <span className={styles.rowText}>
            <span className={styles.rowTitle}>Welcome</span>
            <span className={styles.rowSummary}>
              Rio introduces itself and the interview. Always included.
            </span>
          </span>
          <span className={styles.minutesFixed}>Included</span>
        </li>
        {config.sections.map((section, index) => {
          const meta = preview.sections.find((s) => s.key === section.key);
          const title = meta?.title ?? section.key;
          const lastOn = section.enabled && !canToggleOff(config, section.key);
          const alwaysOn = ALWAYS_ON.has(section.key);
          const fixed = section.key === FIXED_LAST;
          const isDragged = drag?.from === index;
          const classes = [
            styles.row,
            styles[`tone-${section.key}`],
            section.enabled ? '' : styles.rowOff,
            isDragged ? styles.dragging : '',
            settling ? styles.settling : '',
            highlight.has(`section:${section.key}`) ? styles.changed : '',
          ].join(' ');
          return (
            <li
              key={section.key}
              ref={(el) => {
                rows.current[index] = el;
              }}
              className={classes}
              style={
                drag
                  ? {
                      transform: shiftFor(index),
                      transition: isDragged ? 'none' : undefined,
                    }
                  : undefined
              }
              data-region={`section:${section.key}`}
            >
              {fixed ? (
                <span aria-hidden />
              ) : (
                <button
                  type="button"
                  className={styles.grip}
                  ref={(el) => {
                    grips.current.set(section.key, el);
                  }}
                  disabled={disabled}
                  aria-label={`Reorder ${title}. Use the up and down arrow keys.`}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  onPointerDown={(e) => handlePointerDown(e, index)}
                  onPointerMove={handlePointerMove}
                  onPointerUp={() => finishDrag(true)}
                  onPointerCancel={() => finishDrag(false)}
                >
                  <GripIcon />
                </button>
              )}
              <span className={styles.marker}>
                <span
                  className={`${styles.dot} ${section.enabled ? '' : styles.dotOff}`}
                />
              </span>
              <span className={styles.rowText}>
                <span
                  className={styles.rowTitle}
                  id={`${uid}-${section.key}-title`}
                >
                  {title}
                </span>
                <span className={styles.rowSummary}>
                  {section.enabled
                    ? meta?.summary
                    : (OFF_LINES[section.key] ??
                      'Skipped. Turn it on to include it.')}
                </span>
                {alwaysOn && (
                  <span className={styles.rowNote}>
                    {ALWAYS_ON_LINES[section.key]}
                  </span>
                )}
              </span>
              {section.enabled ? (
                <span
                  className={styles.stepper}
                  role="group"
                  aria-label={`${title} length`}
                >
                  <button
                    type="button"
                    className={styles.iconButton}
                    aria-label={`One minute less for ${title}`}
                    disabled={disabled || !canStep(config, section.key, -1)}
                    onClick={() => onStep(section.key, -1)}
                  >
                    <MinusIcon />
                  </button>
                  <span className={styles.minutes} aria-live="polite">
                    {section.minutes} min
                  </span>
                  <button
                    type="button"
                    className={styles.iconButton}
                    aria-label={`One minute more for ${title}`}
                    disabled={disabled || !canStep(config, section.key, 1)}
                    onClick={() => onStep(section.key, 1)}
                  >
                    <PlusIcon />
                  </button>
                </span>
              ) : (
                <span className={styles.minutesFixed}>Off</span>
              )}
              {alwaysOn ? (
                <span
                  className={styles.locked}
                  role="img"
                  aria-label={`${title} is always on`}
                  title="Always on"
                >
                  <LockIcon />
                </span>
              ) : (
                <button
                  type="button"
                  role="switch"
                  className={styles.switch}
                  aria-checked={section.enabled}
                  aria-labelledby={`${uid}-${section.key}-title`}
                  title={lastOn ? 'At least one section stays on' : undefined}
                  disabled={disabled || lastOn}
                  onClick={() => onToggle(section.key)}
                />
              )}
            </li>
          );
        })}
        <li className={`${styles.row} ${styles.rowFixed}`}>
          <span aria-hidden />
          <span className={styles.marker}>
            <span className={`${styles.dot} ${styles.dotFixed}`} />
          </span>
          <span className={styles.rowText}>
            <span className={styles.rowTitle}>Wrap-up</span>
            <span className={styles.rowSummary}>
              Anything to add, and what happens next. Always included.
            </span>
          </span>
          <span className={styles.minutesFixed}>Included</span>
        </li>
      </ol>
      <p className={styles.srOnly} aria-live="polite">
        {announcement}
      </p>
    </>
  );
};

export default SectionTimeline;
